// ⚠️ If you modify the styling of this component and you're using the SectionListings component in your marketplace (featured listings)
// please reflect those changes in the calculateCarouselHeight function in SectionListing.js to avoid layout issues
import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { useIntl } from '../../util/reactIntl';
import { requireListingImage } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { createSlug } from '../../util/urlHelpers';

import {
  AspectRatioWrapper,
  NamedLink,
  ResponsiveImage,
  ProtectedResponsiveImage,
  ListingCardThumbnail,
  IconHeart,
} from '../../components';

import { getListingCardTranslations } from './ListingCard.helpers';

import css from './ListingCard.module.css';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });
const LazyProtectedImage = lazyLoadWithDimensions(ProtectedResponsiveImage, {
  loadAfterInitialRendering: 3000,
});

/**
 * ListingCardImage
 * Component responsible for rendering the image part of the listing card.
 * Joodaic uses ProtectedResponsiveImage to discourage easy image downloads of artwork.
 */
const ListingCardImage = props => {
  const {
    listing,
    setActivePropsMaybe,
    title,
    renderSizes,
    aspectWidth,
    aspectHeight,
    variantPrefix,
    aspectRatioClassName,
    lazyLoadImage,
  } = props;

  const firstImage = listing?.images?.[0] || null;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const aspectRatioClass = aspectRatioClassName || css.aspectRatioWrapper;
  const ImageComponent = lazyLoadImage ? LazyProtectedImage : ProtectedResponsiveImage;

  return (
    <AspectRatioWrapper
      className={aspectRatioClass}
      width={aspectWidth}
      height={aspectHeight}
      {...setActivePropsMaybe}
    >
      <ImageComponent
        rootClassName={css.rootForImage}
        alt={title}
        image={firstImage}
        variants={variants}
        sizes={renderSizes}
      />
    </AspectRatioWrapper>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.aspectRatioClassName custom className for AspectRatioWrapper component
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @param {boolean?} props.showLikesCount
 * @param {Object?} props.currentUser - The current user object
 * @param {number?} props.position - Position in the list for tracking
 * @param {string?} props.source - Source of the listing display for tracking
 * @param {Object?} props.trackingData - Additional tracking data
 * @param {Function?} props.onClick - Click handler for tracking
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();

  const {
    className,
    rootClassName,
    aspectRatioClassName,
    darkMode,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    showLikesCount = true,
    lazyLoadImage = true,
    currentUser,
    position,
    source,
    trackingData,
    onClick,
  } = props;

  const translations = getListingCardTranslations(listing, config, intl);
  const {
    titlePlain,
    titleFormatted,
    cardAriaLabel,
    showPrice,
    priceTooltip,
    priceMessage,
    authorName,
  } = translations;

  const classes = classNames(rootClassName || css.root, className);

  const id = listing?.id?.uuid;
  const { title = '', publicData, metadata = {} } = listing?.attributes || {};
  const slug = createSlug(title);

  const { listingType, cardStyle } = publicData || {};
  const validListingTypes = config.listing.listingTypes || [];
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  // Render the listing image only if listing images are enabled in the listing type
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  // Sets the listing as active in the search map when hovered (if the search map is enabled)
  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(listing?.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  // Handle click events for tracking (Joodaic analytics)
  const handleClick = () => {
    if (onClick) {
      // Track both click and detail view since user is navigating to detail page
      onClick(listing, position, 'click');
      onClick(listing, position, 'detail_view');
    }
  };

  // Joodaic: always show the heart icon for wishlist toggling
  const showHeartIcon = true;
  const likesCount = metadata.likesCount || 0;

  return (
    // Outer div carries onClick for Joodaic analytics tracking on card-area clicks.
    // The NamedLink inside provides the actual navigation + aria-label.
    /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
    <div onClick={handleClick}>
    <NamedLink
      className={classes}
      name="ListingPage"
      params={{ id, slug }}
      ariaLabel={cardAriaLabel}
    >
      {showListingImage ? (
        <ListingCardImage
          renderSizes={renderSizes}
          title={titlePlain}
          listing={listing}
          setActivePropsMaybe={setActivePropsMaybe}
          aspectWidth={aspectWidth}
          aspectHeight={aspectHeight}
          variantPrefix={variantPrefix}
          aspectRatioClassName={aspectRatioClassName}
          lazyLoadImage={lazyLoadImage}
        />
      ) : (
        <ListingCardThumbnail
          style={cardStyle}
          listingTitle={title}
          className={aspectRatioClassName}
          width={aspectWidth}
          height={aspectHeight}
          setActivePropsMaybe={setActivePropsMaybe}
        />
      )}
      <div className={css.info}>
        <div className={css.mainInfo}>
          <div className={css.titleRow}>
            {showListingImage && (
              <div className={classNames(css.title, { [css.lightText]: darkMode })}>
                {titleFormatted}
              </div>
            )}
            {showHeartIcon && id && (
              <IconHeart
                className={css.inlineHeartIcon}
                listingId={id}
                initialLikesCount={likesCount}
                showLikesCount={showLikesCount}
              />
            )}
          </div>
          <div className={css.priceAndAuthor}>
            {showAuthorInfo ? (
              <div className={classNames(css.authorInfo, { [css.lightText]: darkMode })}>
                {authorName}
              </div>
            ) : null}
            {showPrice ? (
              <div className={css.price} title={priceTooltip}>
                {priceMessage}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </NamedLink>
    </div>
  );
};

export default ListingCard;
